import React, { useContext, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';

import { Button, theme, Modal, BuyerAgreementCheckbox } from 'components';
import { useBreakpoint } from 'hooks';
import { BuyerAgreementContext } from 'contexts';
import { featureEnabled } from 'utils';
import { Icon } from '@bb/elements-web';
import TourEditContext from '../TourEditContext';
import ShowingRequestModal from './ShowingRequestsModal';
import useHideChat from '../hooks/useHideChat';

import { TourAutoSaveStatus } from '../types';

const SendRequests: React.FC = () => {
  const { autoSaveStatus, tourItems, hasNarBuyerSettlement } =
    useContext(TourEditContext);
  const { hasBuyerAgreed } = useContext(BuyerAgreementContext);
  const [visible, setVisible] = useState(false);
  const { md } = useBreakpoint();
  useHideChat(!md);
  const buttonRef = useRef(null);

  const showModal = () => {
    buttonRef?.current?.blur();
    if (
      featureEnabled('narBuyerSettlementFeature') &&
      hasNarBuyerSettlement &&
      !hasBuyerAgreed
    ) {
      Modal.warning({
        title: 'Written buyer agreement reminder',
        content:
          'A participant working with a buyer must enter into a written agreement with the buyer prior to the buyer touring any home.',
        okText: 'Yes, I understand',
        cancelText: 'Cancel',
        okCancel: true,
        centered: true,
        width: '450px',
        icon: <Icon icon="infoCircleOutlined" color="danger" size={20} />,
        onOk: () => setVisible(true),
      });
      return;
    }
    setVisible(true);
  };
  const isRequestBtnEnabled = useMemo(
    () =>
      // Send Requests button should be enabled if there are any draft items in the tour that has a selected time
      // AND autosave is in saved status
      tourItems.some(
        tourItem => tourItem.type === 'APPOINTMENT' && tourItem.time,
      ) && autoSaveStatus === TourAutoSaveStatus.SAVED,
    [autoSaveStatus, tourItems],
  );

  return (
    <StyledContainer $md={md}>
      {hasNarBuyerSettlement && <BuyerAgreementCheckbox isTour />}
      <StyledButton
        data-cy="tours-send-requests-button"
        disabled={!isRequestBtnEnabled}
        onClick={showModal}
        ref={buttonRef}
      >
        {autoSaveStatus !== TourAutoSaveStatus.SAVED
          ? 'Saving...'
          : 'Send Requests'}
      </StyledButton>
      <ShowingRequestModal visible={visible} setVisible={setVisible} />
    </StyledContainer>
  );
};

export default SendRequests;

const StyledContainer = styled.div<{ $md: boolean }>`
  border-top: 1px solid #f0f0f0;
  padding: ${({ $md }) => ($md ? '20px' : '20px 12px')};
  margin-top: auto;
  background-color: white;
  z-index: 900;

  ${({ $md }) =>
    !$md &&
    `
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  `}
`;

const StyledButton = styled(Button)`
  color: white;
  font-weight: 400;
  background-color: ${theme.blue6};
  border-radius: 2px;
  width: 100%;
  height: 40px;
`;
